import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  BILLING_AWARD_KINDS,
  billingAwardRuleProblem,
  billingAwardsOf,
  businessDate,
  fromColumn,
  loadFailureMessage,
  sum,
  ZERO,
  type BillingAwardKind,
  type BillingAwardRule,
  type BillingAwardsResult,
  type BillingAwardTier,
  type Money,
} from '@evogard/domain';
import type { BillingAwardPeriodView, BillingAwardRuleWire, BillingAwardsResultWire } from '@evogard/contract';
import { api } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';

export const AWARDS_KEY = ['cobranca', 'premiacao'] as const;

export interface AwardTenant {
  id: number;
  slug: string;
  name: string;
}

/** As bases da pessoa — a premiação pede uma de cada vez e soma. */
export function useAwardTenants(): AwardTenant[] {
  const { data: context } = useSessionContext();
  return (context?.tenants ?? []).map((t) => ({ id: t.id, slug: t.slug, name: t.nome }));
}

export function useInvalidateAwards() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: AWARDS_KEY });
}

/** A competência corrente, pelo relógio da casa. */
export const currentPeriod = () => businessDate(new Date()).slice(0, 7);

/** As últimas doze competências, da mais nova para a mais velha. */
export function recentPeriods(): string[] {
  const [y, m] = currentPeriod().split('-').map(Number) as [number, number];
  return Array.from({ length: 12 }, (_, i) => new Date(Date.UTC(y, m - 1 - i, 1)).toISOString().slice(0, 7));
}

export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** A régua do fio (dinheiro em texto) na forma do domínio (centavos). */
export function ruleFromWire(wire: BillingAwardRuleWire): BillingAwardRule {
  const tiers = Object.fromEntries(BILLING_AWARD_KINDS.map((k) => [k, [] as BillingAwardTier[]])) as Record<
    BillingAwardKind,
    BillingAwardTier[]
  >;
  for (const t of wire.tiers) {
    tiers[t.kind].push({ label: t.label, minAmount: fromColumn(t.minAmount)!, bonus: fromColumn(t.bonus)! });
  }
  return { enabled: wire.enabled, tiers };
}

/** O resultado congelado do fio (dinheiro em texto) na forma do domínio. */
export function resultFromWire(wire: BillingAwardsResultWire): BillingAwardsResult {
  const money = (v: string) => fromColumn(v)!;
  const tier = (t: { label: string; minAmount: string; bonus: string } | null) =>
    t === null ? null : { label: t.label, minAmount: money(t.minAmount), bonus: money(t.bonus) };
  return {
    operators: wire.operators.map((o) => ({
      operatorId: o.operatorId,
      recovered: money(o.recovered),
      paidCount: o.paidCount,
      byTenant: o.byTenant.map((t) => ({ tenantId: t.tenantId, count: t.count, recovered: money(t.recovered) })),
      byType: o.byType.map((t) => ({ typeLabel: t.typeLabel, count: t.count, recovered: money(t.recovered) })),
      days: o.days.map((d) => ({ date: d.date, recovered: money(d.recovered), tier: tier(d.tier), bonus: money(d.bonus) })),
      dailyBonus: money(o.dailyBonus),
      weeks: o.weeks.map((w) => ({
        start: w.start,
        end: w.end,
        recovered: money(w.recovered),
        tier: tier(w.tier),
        bonus: money(w.bonus),
      })),
      weeklyBonus: money(o.weeklyBonus),
      monthlyTier: tier(o.monthlyTier),
      monthlyBonus: money(o.monthlyBonus),
      total: money(o.total),
    })),
    leadership:
      wire.leadership === null
        ? null
        : { recovered: money(wire.leadership.recovered), tier: tier(wire.leadership.tier), bonus: money(wire.leadership.bonus) },
  };
}

/** Uma base: a competência dela, ou por que não veio. */
export type AwardTenantState =
  | { tenant: AwardTenant; state: 'pronta'; view: BillingAwardPeriodView }
  | { tenant: AwardTenant; state: 'sem_permissao' }
  | { tenant: AwardTenant; state: 'erro'; message: string }
  | { tenant: AwardTenant; state: 'carregando' };

export function useAwardPeriod(period: string): AwardTenantState[] {
  const tenants = useAwardTenants();
  const results = useQueries({
    queries: tenants.map((t) => ({
      queryKey: [...AWARDS_KEY, 'competencia', t.slug, period],
      queryFn: async (): Promise<BillingAwardPeriodView | null> => {
        const r = await api.billingAwards.period({ params: { tenant: t.slug }, query: { period } });
        if (r.status === 403) return null;
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, `a premiação da ${t.name}`));
        return r.body;
      },
    })),
  });
  return tenants.map((tenant, i): AwardTenantState => {
    const r = results[i]!;
    if (r.error) return { tenant, state: 'erro', message: r.error.message };
    if (r.data === null) return { tenant, state: 'sem_permissao' };
    if (r.data) return { tenant, state: 'pronta', view: r.data };
    return { tenant, state: 'carregando' };
  });
}

/** Uma linha do resultado, com o nome e o que veio de cada base. */
export interface AwardOperatorLine {
  id: string;
  name: string;
  records: number;
  monitoring: number;
  invalidated: number;
  byTenant: Record<string, Money>;
}

export type AwardsMerge =
  | { state: 'vazio' }
  | { state: 'recusado'; problem: string }
  | {
      state: 'calculado';
      result: BillingAwardsResult;
      rule: BillingAwardRule;
      lines: AwardOperatorLine[];
      /** As duas bases fechadas: só então o relatório de pagamento sai. */
      closed: boolean;
      /** O mês veio do sistema antigo com o valor congelado: o resultado é o de lá, e não um cálculo. */
      frozen: boolean;
      /** A base que não respondeu — o relatório não sai sobre metade do dinheiro. */
      missing: AwardTenant[];
    };

/**
 * As bases somadas, e a faixa aplicada sobre a soma (G6).
 *
 * A régua tem de ser a mesma nas bases que responderam
 * (`billingAwardRuleProblem`); se não for, nada é calculado — escolher a régua
 * de uma base seria decidir o que ninguém decidiu.
 */
export function mergeAwards(states: readonly AwardTenantState[]): AwardsMerge {
  const ready = states.flatMap((b) => (b.state === 'pronta' ? [b] : []));
  if (ready.length === 0) return { state: 'vazio' };
  const rules = ready.map((b) => ({ tenant: b.tenant.name, rule: ruleFromWire(b.view.rule) }));
  const problem = billingAwardRuleProblem(rules);
  if (problem !== null) return { state: 'recusado', problem };

  const names = new Map<string, string>();
  for (const b of ready) for (const o of b.view.operators) names.set(o.id, o.name);
  /*
   * O mês trazido do sistema antigo guarda o VALOR, das duas bases somadas: ele
   * é o mês, e não se recalcula (decisão do dono, 23/10/2026). Uma base que o
   * tenha basta.
   */
  const frozen = ready.find((b) => b.view.closing?.result)?.view.closing?.result ?? null;
  const links = ready.flatMap((b) =>
    b.view.links.map((l) => ({
      tenantId: b.tenant.id,
      operatorId: l.operatorId,
      period: l.period,
      settledOn: l.settledOn,
      recovered: fromColumn(l.recovered)!,
      typeLabel: l.typeLabel,
    })),
  );
  const byName = (a: string, b: string) => (names.get(a) ?? a).localeCompare(names.get(b) ?? b);
  const result = frozen
    ? resultFromWire(frozen)
    : billingAwardsOf({ period: ready[0]!.view.period, rule: rules[0]!.rule, operatorIds: [...names.keys()].sort(byName), links });
  const operatorIds = result.operators.map((o) => o.operatorId).sort(byName);

  const lines = operatorIds.map((id): AwardOperatorLine => {
    const stats = ready.flatMap((b) => b.view.stats.filter((s) => s.operatorId === id));
    const op = result.operators.find((o) => o.operatorId === id)!;
    const byTenant: Record<string, Money> = {};
    for (const b of ready) {
      byTenant[b.tenant.slug] = op.byTenant.find((t) => t.tenantId === b.tenant.id)?.recovered ?? ZERO;
    }
    return {
      id,
      name: names.get(id) ?? id,
      records: stats.reduce((n, s) => n + s.records, 0),
      monitoring: stats.reduce((n, s) => n + (s.byStatus.monitorando ?? 0), 0),
      invalidated: stats.reduce((n, s) => n + s.invalidated, 0),
      byTenant,
    };
  });
  return {
    state: 'calculado',
    result,
    rule: rules[0]!.rule,
    lines,
    closed: ready.length === states.length && ready.every((b) => b.view.closing !== null),
    frozen: frozen !== null,
    missing: states.filter((b) => b.state !== 'pronta').map((b) => b.tenant),
  };
}

export const totalOf = (values: readonly Money[]) => sum(...values);
